import {UI} from "../../../../../../stem-core/src/ui/UIBase";

import {styleRule, StyleSheet} from "../../../../../../stem-core/src/ui/Style";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {countriesDataService} from "../../../../../services/CountriesDataService";
import {Messages} from "../../../../../Messages";
import {NOOP_FUNCTION} from "../../../../../../stem-core/src/base/Utils";
import {SUBSCRIBE_PLAN_STATE} from "../../../../flows/steps/SubscriptionPlanFlowStep.js";
import {Money} from "../../../../../../stem-core/src/localization/Money.js";
import {isSmallScreen} from "../../../../../UtilsLib";
import {INLINE_EDIT_SUBSCRIPTION_URLS} from "../../../../PanelConstants";
import {Router} from "../../../../../../stem-core/src/ui/Router";
import {BUTTON_TYPE} from "../../../../../Constants";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {Flow} from "../../../../flows/Flow.js";
import {iFrameState} from "../../../../../services/IFrameState.js";
import {BlinkButton} from "../../../../../../core/ui/Button.jsx";


class TNRSubscriptionCoverageCardStyle extends StyleSheet {
    @styleRule
    container = {
        minHeight: () => isSmallScreen() ? "" : "484px",
        padding: "18px 24px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: this.themeProps.WHITE,
        border: "1px solid #DDDDDD",
        width: () => isSmallScreen() ? null : 418,
    };

    @styleRule
    bestValueHeader = {
        textAlign: "center",
        width: "100%",
        padding: 10,
        top: 0,
        left: 0,
        position: "absolute",
        background: () => this.themeProps.HERO_CARD_BORDER_COLOR || "#D6D6D6",
        ...iFrameState.iframeParams.bestValueHeader
    };

    @styleRule
    bestValueHeaderGuard = {
        height: 42,
    };

    @styleRule
    bestValueCard = {
        borderColor: this.themeProps.HERO_CARD_BORDER_COLOR || "#D6D6D6",
        boxShadow: () => `inset 0 0 0 4px ${this.themeProps.HERO_CARD_BORDER_COLOR || "#D6D6D6"}`,
    };

    @styleRule
    title = {
        fontSize: 24,
        fontWeight: 600,
    };

    @styleRule
    cycleDuration = {
        fontSize: 20,
        marginBottom: 18,
    };

    @styleRule
    pricesBreakdown = {
        width: "100%",
        padding: "12px 6px 0 0",
        borderTop: () => "1px solid #E5E5E5",
        display: "flex",
        justifyContent: "space-between",
        fontSize: 18,
    };

    @styleRule
    bestValueBelowPrice = {
        fontSize: 14,
    };

    @styleRule
    afterPricesBreakdown = {
        width: "100%",
        paddingTop: 12,
        borderBottom: () => "1px solid #E5E5E5",
    };

    @styleRule
    pricesBreakdownEntry = {
        padding: 6,
        paddingBottom: 0,
        ":first-child": {
            paddingLeft: 0,
        },
        ":last-child": {
            paddingRight: 0,
        }
    };

    @styleRule
    perkText = {
        fontSize: 16,
        marginTop: 12,
    };

    @styleRule
    perkSubtext = {
        marginLeft: 24,
    };

    @styleRule
    perk = {
        marginTop: 8,
        ":last-child": {
            marginBottom: 18,
        }
    };

    @styleRule
    button = {
        width: "100%",
        bottom: 24,
        ...iFrameState.iframeParams.buttonStyle
    };

    @styleRule
    subscriptionPerksList = {
        marginTop: 18,
        marginBottom: -4,
        fontSize: 16,
    };
}

// TODO: Extend SubscriptionCoverageCard at this point.
@registerStyle(TNRSubscriptionCoverageCardStyle)
export class TNRSubscriptionCoverageCard extends UI.Element {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            offer: null,
            bestValue: false,
            onSubscriptionOfferSelected: NOOP_FUNCTION,
            testId: "subscriptionCoverageCard",
        };
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        if (this.options.bestValue) {
            attr.addClass(this.styleSheet.bestValueCard);
        }
    }

    getOfferLabel() {
        if (Flow.subscriptionPlan.subscriptionPlanState === SUBSCRIBE_PLAN_STATE.subscribe) {
            return Messages.subscribe;
        }
        return Messages.changeSubscription;
    }

    userHasEquivalentSubscription(offer) {
        if (Flow.subscriptionPlan.subscriptionPlanState === SUBSCRIBE_PLAN_STATE.subscribe) {
            return false;
        }
        return Flow.subscriptionPlan.userHasEquivalentSubscriptionToOffer(offer);
    }

    getPricesBreakdown(offer) {
        if (!offer.shouldCollectAddress() || !offer.hasShippingPrice()) {
            return [[
                offer.getName(),
                offer.getPrice(),
            ]];
        }

        return offer.billingPlan.shippingPrices.map(({countryCode, amount}) => [
            countriesDataService.getCountryByCode(countryCode)?.name || Messages.international,
            offer.getPrice().add(new Money(amount, offer.getCurrency()))
        ])
    }

    renderBestValueCardHeader() {
        const {styleSheet} = this;
        const {bestValue} = this.options;

        return bestValue && [
            <div className={styleSheet.bestValueHeader}>
                {Messages.bestValue}
            </div>,
            <div className={styleSheet.bestValueHeaderGuard}/>
        ];
    }

    renderNameAndDurationSection() {
        const {styleSheet} = this;
        const {offer} = this.options;
        const coverage = offer.getCoverage();

        //Direct mail inline subscription hack
        if (iFrameState.iframeParams.subscriptionOfferConfigs &&
            iFrameState.iframeParams.subscriptionOfferConfigs[offer.id]?.useCycleDurationAsName) {
            return <div className={styleSheet.title}>
                {offer.billingPlan.getCycleDuration()}
            </div>;
        }

        return [
            <div className={styleSheet.title}>
                {coverage.getName()}
            </div>,
            <div className={styleSheet.cycleDuration}>
                {offer.billingPlan.getCycleDuration()}
            </div>,
        ];
    }

    renderPriceBreakdownSection() {
        const {styleSheet} = this;
        const {offer, bestValue} = this.options;

        //Direct mail inline subscription hack
        if (iFrameState.iframeParams.subscriptionOfferConfigs &&
            iFrameState.iframeParams.subscriptionOfferConfigs[offer.id]?.useOfferNameAsPriceBreakdown) {
            return <div className={styleSheet.pricesBreakdown}>
                <MessageElement message={offer.getName()} />
            </div>;
        }

        return [
            <div className={styleSheet.pricesBreakdown}>
                {this.getPricesBreakdown(offer).map(([offerName, price], index) =>
                    <div className={styleSheet.pricesBreakdownEntry}>
                        <div>{offerName === "Digital" ? "Worldwide" : offerName}</div>
                        <div>{index === 0 && bestValue ? <b>{price}</b> : price}</div>
                    </div>)}
            </div>,
            bestValue && <MessageElement className={styleSheet.bestValueBelowPrice} message={Messages.bestValueBelowPrice} />
        ];
    }

    renderSubscriptionPerksSection() {
        const {styleSheet} = this;
        const {offer} = this.options;
        const coverage = offer.getCoverage();
        const iframeParams = iFrameState.iframeParams;

        //Direct mail inline subscription hack
        const configPerks = iframeParams.subscriptionOfferConfigs ? iframeParams.subscriptionOfferConfigs[offer.id]?.perks : null;
        const items = configPerks ? configPerks.map((perk) => { return {"text": perk}; }) : coverage.getDescription().items || [];

        return [
            <div className={styleSheet.subscriptionPerksList}>{Messages.subscriptionPerksList}</div>,
            <div>
                {items.map(({text, subtext}) =>
                    <div className={styleSheet.perk}>
                        <li className={styleSheet.perkText}>{text}</li>
                        <div className={styleSheet.perkSubtext}>{subtext}</div>
                    </div>
                )}
            </div>
        ];
    }

    renderSubscribeButton() {
        const {styleSheet} = this;
        const {offer, onSubscriptionOfferSelected} = this.options;

        return this.userHasEquivalentSubscription(offer) ?
            <BlinkButton onClick={() => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel)} type={BUTTON_TYPE.SECONDARY}>
                {Messages.cancelSubscription}
            </BlinkButton> :
            <BlinkButton className={styleSheet.button} onClick={() => onSubscriptionOfferSelected(offer)}>
                {this.getOfferLabel()}
            </BlinkButton>;
    }

    render() {
        const {styleSheet} = this;

        return [
            this.renderBestValueCardHeader(),
            this.renderNameAndDurationSection(),
            this.renderPriceBreakdownSection(),
            <div className={styleSheet.afterPricesBreakdown} />,
            this.renderSubscriptionPerksSection(),
            <div style={{flex: 1}}/>,
            this.renderSubscribeButton()
        ];
    }

    async onMount() {
        super.onMount();

        // TODO @cleanup we do this to have the country names for the offer.
        await countriesDataService.ensureLoaded();
        this.redraw();
    }
}
